'use client'

import * as React from 'react'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { type VariantProps, cva } from 'class-variance-authority'
import Spinner from '../icons/spinner.js'
import { cn } from '../helpers/index.js'

export const sizeMap = {
  sm: {
    width: '16',
    height: '16',
  },
  base: {
    width: '18',
    height: '18',
  },
  lg: {
    width: '20',
    height: '20',
  },
}
const LinkButtonAppendIcon = ({ size = 'base', ...rest }: { size: 'base' | 'lg' | 'sm'; className?: string }) => {
  const { width, height } = sizeMap[size]
  return (
    <svg width={width} height={height} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...rest}>
      <path
        d="M7.5 5L12.5 10L7.5 15"
        stroke="currentColor"
        strokeWidth="1.66667"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/**
 * Button Variants
 */
// eslint-disable-next-line tailwindcss/enforces-negative-arbitrary-values
const buttonVariants = cva(
  'rounded-btn inline-flex cursor-pointer items-center justify-center text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none',
  {
    variants: {
      variant: {
        primary:
          'bg-btn-primary text-btn-primary-foreground hover:bg-btn-primary-active hover:text-btn-primary-active-foreground hover:bg-gradient-brand border-none ',
        secondary:
          'bg-btn-secondary outline-btn-primary text-btn-secondary-foreground hover:bg-btn-secondary-active hover:text-btn-secondary-active-foreground border-solid outline outline-[1.6px] outline-offset-[-1.6px]',
        link: 'text-info-primary hover:text-btn-primary-active border-none',
        ghost: 'text-info-primary hover:bg-btn-primary hover:text-btn-primary-foreground',
      },
      size: {
        sm: 'px-3 py-2 text-[12px] leading-tight',
        base: 'px-5 pb-[10px] pt-[11px] text-[14px] leading-tight',
        lg: 'lg-desktop:px-7 lg-desktop:pb-[14px] lg-desktop:pt-[15px] lg-desktop:text-[16px] px-5 pb-[10px] pt-[11px] text-[14px] leading-tight',
        icon: 'size-10 rounded-full leading-tight',
      },
      /** hover 状态下的动画效果,需要注意不同variant 下的效果 */
      hoverEffect: {
        none: '',
        slide: '',
      },
      state: {
        default: '',
        disabled: '',
        loading: '',
      },
    },
    compoundVariants: [
      {
        variant: 'primary',
        hoverEffect: 'slide',
        class:
          'before:rounded-btn hover:!bg-btn-primary before:bg-btn-primary-active relative z-[1] overflow-hidden before:absolute before:inset-y-0 before:-z-[1] before:w-full before:-translate-x-full before:transition-transform before:duration-300 before:ease-in-out before:content-[""] hover:before:translate-x-0',
      },
      {
        variant: 'secondary',
        hoverEffect: 'slide',
        class:
          'before:rounded-btn hover:!bg-btn-secondary before:bg-btn-secondary-active relative z-[1] overflow-hidden before:absolute before:inset-y-0 before:-z-[1]  before:w-full before:-translate-x-full before:transition-transform before:duration-300 before:ease-in-out before:content-[""] hover:before:translate-x-0',
      },
      {
        variant: 'primary',
        state: 'loading',
        class: 'bg-btn-primary-active disabled:border-btn-primary-disabled-border',
      },
      {
        variant: 'secondary',
        state: 'loading',
        class:
          'bg-btn-secondary disabled:bg-btn-secondary-disabled disabled:text-btn-secondary-disabled-foreground disabled:border-btn-primary-disabled',
      },
      {
        variant: 'primary',
        state: 'disabled',
        class:
          'disabled:bg-btn-primary-disabled disabled:text-btn-primary-disabled-foreground disabled:border-btn-primary-disabled',
      },
      {
        variant: 'secondary',
        state: 'disabled',
        class:
          'disabled:bg-btn-secondary-disabled disabled:text-btn-secondary-disabled-foreground disabled:border-btn-secondary-disabled-border disabled:outline-btn-secondary-disabled-border',
      },
      {
        variant: 'link',
        state: 'disabled',
        class: 'disabled:text-info-quaternary',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
      hoverEffect: 'none',
      state: 'default',
    },
  }
)

const loadingSvgVariants = cva('', {
  variants: {
    variant: {
      primary: 'stroke-btn-primary-foreground',
      secondary: 'stroke-btn-secondary-foreground',
      link: 'stroke-primary',
      ghost: 'stroke-primary',
    },
  },
  defaultVariants: {
    variant: 'primary',
  },
})

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** 设置按钮载入状态  */
  loading?: boolean
  /** 设置按钮失效状态  */
  disabled?: boolean
  /** 定义自组件作为父组件的类型， 详细使用方式请参考radix-ui (已作废请勿使用) */
  asChild?: boolean
  /** 定义自组件作为父组件的类型， 详细使用方式请参考radix-ui */
  as?: 'button' | 'a'
  href?: string
  iconClassName?: string
  /** 自定义loading  */
  spinner?: React.ReactNode
}

/**
 * Button - 按钮
 *
 * @description 可点击的交互式按钮组件，支持多种样式、大小、状态和加载效果
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      // eslint-disable-next-line
      asChild = false,
      as = 'button',
      href,
      size = 'lg',
      children,
      variant,
      hoverEffect = 'none',
      className,
      disabled = false,
      loading = false,
      spinner,
      iconClassName,
      ...props
    }: ButtonProps,
    ref
  ) => {
    const Comp = as
    const isInactive = disabled || loading
    const state = loading ? 'loading' : disabled ? 'disabled' : 'default'

    return (
      <Comp
        disabled={isInactive}
        className={cn(buttonVariants({ variant, size, hoverEffect, state }), className)}
        ref={ref as any}
        {...(as === 'a' ? { href } : {})}
        {...(props as any)}
      >
        {loading ? (
          <>
            <span style={{ display: 'contents', visibility: 'hidden' }} aria-hidden>
              {children}
            </span>
            <span className="absolute">
              {spinner ? (
                spinner
              ) : (
                <Spinner
                  className={loadingSvgVariants({
                    variant,
                  })}
                />
              )}
            </span>
            <VisuallyHidden>{children}</VisuallyHidden>
          </>
        ) : (
          <>
            {children}
            {variant === 'link' && size && size !== 'icon' && (
              <LinkButtonAppendIcon className={iconClassName} size={size} />
            )}
          </>
        )}
      </Comp>
    )
  }
)

Button.displayName = 'Button'
export default Button
